import React, { useRef } from 'react';
import { motion, useScroll, useTransform } from 'framer-motion';
import { Award, Zap, Heart, Users } from 'lucide-react';
import { useLang } from '../i18n/LangContext';
import { SectionEyebrow, SectionTitle, FadeIn, StaggerList, StaggerItem } from '../components/Animated';

const OFFICE_IMG = 'https://images.pexels.com/photos/14963655/pexels-photo-14963655.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940';

const AboutPage = () => {
  const { t } = useLang();
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ['start end', 'end start'] });
  const y = useTransform(scrollYProgress, [0, 1], [-50, 80]);

  return (
    <div data-testid="about-page" className="relative pt-32 pb-24">
      <div className="absolute inset-0 bg-grid bg-grid-radial pointer-events-none opacity-40" />
      <div className="relative max-w-7xl mx-auto px-6 md:px-12">
        <FadeIn><SectionEyebrow>{t('about.eyebrow')}</SectionEyebrow></FadeIn>
        <SectionTitle className="mt-3">{t('about.title')}</SectionTitle>

        <div className="mt-16 grid lg:grid-cols-12 gap-12 items-start">
          <FadeIn className="lg:col-span-7 space-y-5 text-lg text-zinc-300 leading-relaxed">
            <p>{t('about.p1')}</p>
            <p>{t('about.p2')}</p>
            <p className="text-white font-semibold">{t('about.p3')}</p>
          </FadeIn>

          <div ref={ref} className="lg:col-span-5 relative h-[520px] overflow-hidden rounded-2xl border border-white/10">
            <motion.img
              src={OFFICE_IMG}
              alt="office"
              style={{ y }}
              className="absolute inset-0 w-full h-[120%] object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black via-black/30 to-transparent" />
            <div className="absolute bottom-6 inset-x-6">
              <p className="heading-font text-white font-bold text-xl">Cairo · Remote · Worldwide</p>
            </div>
          </div>
        </div>

        {/* Values */}
        <div className="mt-32">
          <SectionEyebrow>{t('about.values_title')}</SectionEyebrow>
          <StaggerList className="mt-10 grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              { icon: Award, k: 'v1' },
              { icon: Zap, k: 'v2' },
              { icon: Heart, k: 'v3' },
              { icon: Users, k: 'v4' },
            ].map(({ icon: Icon, k }, i) => (
              <StaggerItem key={k}>
                <div className="tech-card rounded-xl border border-white/10 bg-zinc-950 p-6 h-full hover:border-blue-500/40 transition">
                  <div className="w-10 h-10 rounded-md bg-blue-600/20 grid place-items-center mb-4">
                    <Icon className="w-5 h-5 text-blue-400" />
                  </div>
                  <h4 className="heading-font text-xl font-bold text-white">{t(`about.${k}_title`)}</h4>
                  <p className="mt-2 text-zinc-400 leading-relaxed">{t(`about.${k}_desc`)}</p>
                </div>
              </StaggerItem>
            ))}
          </StaggerList>
        </div>
      </div>
    </div>
  );
};

export default AboutPage;
